import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { OrganizationState } from '@state/organization.state';
import { QuestionnaireState } from '@state/questionnaire.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { DCarbonizeAppConfig } from 'app/@types/shared/app-config';
import { ConfigService } from 'app/app-config.service';
import posthog from 'posthog-js';
import { filter, map } from 'rxjs/operators';
import { AnalyticsService } from './analytics.service';

/**
 * Properties attached to a PostHog event. Deliberately narrow: usage metadata
 * only — never questionnaire answers, emission values or other client data.
 */
export type PostHogEventProperties = Record<string, string | number | boolean | undefined>;

/**
 * Service responsible for the PostHog product analytics integration.
 *
 * Answers the "how is the application actually used" questions: which pages a
 * client opens, how often they come back, which parts of the product they never
 * open. Page paths are routed through `AnalyticsService.getPagePath`, so emails
 * and uuids are stripped before anything leaves the browser.
 *
 * A *client* is an organization. `organization_name` — stamped on every event —
 * is therefore the property usage should be read by. `tenant` sits one level
 * above: it is the white-label platform the build belongs to (D-Carbonize,
 * Belfius, …), several organizations wide, and only useful for comparing
 * deployments.
 *
 * Nothing here runs without consent: `setAnalyticsConsent` is the only entry
 * point, driven by `ConsentService`. Until it is called with `true` the SDK is
 * never initialized, so no cookie and no local storage entry is written.
 */
@Injectable({
    providedIn: 'root',
})
export class PostHogService extends AnalyticsService {
    private config: DCarbonizeAppConfig = this.configService.getConfig;
    private isInitialized = false;
    /**
     * Last claims seen for the signed-in user, kept for the whole session.
     * Consent can arrive after sign-in, and can be withdrawn and granted again
     * — both leave the SDK anonymous — so the identity has to be replayable,
     * not just deferred once. Cleared on logout by `reset`.
     */
    private lastClaims?: UserClaims;

    constructor(
        private configService: ConfigService,
        private router: Router,
        private store: Store,
    ) {
        super();
    }

    /**
     * Applies the user's analytics decision. Granting initializes the SDK on
     * first use, resumes capture and re-attaches the signed-in identity;
     * withdrawing stops capture and drops the identified person.
     */
    public setAnalyticsConsent(granted: boolean): void {
        if (!granted) {
            if (this.isInitialized) {
                posthog.opt_out_capturing();
                posthog.reset();
            }

            return;
        }

        this.activate();

        if (!this.isInitialized) {
            return;
        }

        posthog.opt_in_capturing();

        // A withdrawal cleared the persistence, super properties included, so the
        // context has to be put back — for a visitor who never signed in, this is
        // the only thing that puts it back.
        this.registerContext();

        // Re-identify, not just on the first grant: a withdrawal calls
        // `posthog.reset()`, so without this every event after a change of mind
        // would be anonymous — no person, no organization group — until the next
        // sign-in. Withdraw-then-accept is exactly what reopening the banner
        // invites, so it is the common path rather than an edge case.
        if (this.lastClaims) {
            this.identify(this.lastClaims);
        }
    }

    /**
     * Initializes PostHog and sets up page view tracking. Only ever reached with
     * consent. A missing key leaves the integration silently dormant — that is
     * how local dev and the not-yet-rolled-out tenant builds opt out.
     */
    public activate(): void {
        const postHogConfig = this.config.postHog;
        if (this.isInitialized || !postHogConfig?.key) {
            return;
        }

        posthog.init(postHogConfig.key, {
            api_host: postHogConfig.host,
            defaults: '2026-08-30',
            // Page views are captured from the router below so that the
            // sanitized path is sent instead of the raw url.
            capture_pageview: false,
            // Anonymous visitors stay anonymous: a person profile is only
            // created once `identify` runs, after login.
            person_profiles: 'identified_only',
            // Element attributes can carry organization ids and answer values.
            mask_all_element_attributes: true,
            // And so can the text next to them — an emission factor name, an
            // organization, the author of a comment. This is a separate switch
            // from the one above and also defaults to false, so autocapture was
            // reading `textContent` off every click. Clicks are still counted,
            // rageclicks still fire; only the label is dropped.
            mask_all_text: true,
            // Session replay records whatever is on screen, which for this
            // product means client emission data. Off until explicitly wanted.
            disable_session_recording: true,
            // Withdrawing consent must leave the browser as clean as never
            // having given it. The SDK defaults this to false, which keeps its
            // storage entry alive and merely rotates the id inside it.
            opt_out_persistence_by_default: true,
        });

        this.registerContext();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => event as NavigationEnd),
            )
            .subscribe((event) => this.capturePageView(event.urlAfterRedirects));

        this.isInitialized = true;
    }

    /**
     * Ties subsequent events to the signed-in user and their organization, so
     * usage can be read per client instead of per anonymous browser.
     */
    public identify(claims: UserClaims): void {
        if (!claims?.user_email) {
            return;
        }

        this.lastClaims = claims;

        if (!this.isInitialized) {
            return;
        }

        posthog.identify(claims.user_email, {
            name: claims.user_name,
            role: claims.userRole,
            organization_name: claims.organization?.name,
            organization_uid: claims.organization?.uid,
        });

        this.registerContext();

        const organization = claims.organization;
        if (organization?.uid) {
            posthog.group('organization', organization.uid, {
                name: organization.name,
                license_level: this.getLicenseLevel(),
            });
        }
    }

    /**
     * Captures a custom product event, e.g. `scan_completed`.
     * @param event - Event name, snake_case
     * @param properties - Usage metadata only
     */
    public capture(event: string, properties?: PostHogEventProperties): void {
        if (!this.isInitialized) {
            return;
        }

        posthog.capture(event, properties);
    }

    /**
     * Captures an event about the questionnaire the user is working on.
     *
     * The unit of analysis is the questionnaire — one organization, one entity,
     * one year — rather than the user or the organization, so these events are
     * grouped by `questionnaire_id`. A key rebuilt from the readable fields
     * instead would split in two the day an entity is renamed, and would merge
     * the scan and the expert questionnaire of the same entity and year. The
     * readable fields travel along anyway, so a result reads without a lookup.
     */
    public captureQuestionnaireEvent(event: string, properties?: PostHogEventProperties): void {
        // Context last: it is the same on every questionnaire event by
        // construction, and a call site must not be able to drift from it.
        this.capture(event, { ...properties, ...this.getQuestionnaireContext() });
    }

    /**
     * Clears the identified person on logout, so the next user signing in on the
     * same browser is not merged into the previous one.
     */
    public reset(): void {
        this.lastClaims = undefined;

        if (!this.isInitialized) {
            return;
        }

        posthog.reset();
        this.registerContext();
    }

    /**
     * Stamps the client context on every subsequent event.
     *
     * `organization_name` is the one that matters — a client is an organization,
     * and a super property is what makes it readable on the events themselves
     * rather than only through the person or the group. `tenant` is the
     * white-label platform above it; `$host` would imply the same thing, but
     * parsing hostnames breaks the day a tenant changes domain.
     *
     * Called again on every identify and on logout because `posthog.reset()`
     * clears the whole persistence, super properties included.
     */
    private registerContext(): void {
        const properties: PostHogEventProperties = { tenant: this.config.client.name };

        const organization = this.lastClaims?.organization;
        if (organization?.name) {
            Object.assign(properties, { organization_name: organization.name });
        }
        if (organization?.uid) {
            Object.assign(properties, { organization_uid: organization.uid });
        }

        posthog.register(properties);
    }

    private capturePageView(url: string): void {
        const pagePath = this.getPagePath(url);
        if (!pagePath) {
            return;
        }

        const properties: PostHogEventProperties = {
            $current_url: `${window.location.origin}${pagePath}`,
            $pathname: pagePath,
        };

        const licenseLevel = this.getLicenseLevel();
        if (licenseLevel) {
            Object.assign(properties, { license_level: licenseLevel });
        }

        posthog.capture('$pageview', properties);
    }

    private getLicenseLevel(): string | undefined {
        return this.store.selectSnapshot(OrganizationState.getPricingPlan)?.name;
    }

    private getQuestionnaireContext(): PostHogEventProperties {
        const questionnaire = this.store.selectSnapshot(QuestionnaireState.getQuestionnaire);
        if (!questionnaire?.id) {
            return {};
        }

        const context: PostHogEventProperties = {
            questionnaire_id: questionnaire.id,
            period_year: questionnaire.refYear,
            entity_id: questionnaire.organizationEntityId,
        };

        const entity = this.store
            .selectSnapshot(OrganizationState.getEntities)
            ?.find((candidate) => candidate.id === questionnaire.organizationEntityId);
        if (entity?.name) {
            Object.assign(context, { entity_name: entity.name });
        }

        return context;
    }
}
